import { NextActionReady, StateFastForward, CompleteAction } from './core';
import { makeTimelineEntry } from './timelineEntries';
import { captureStateSnapshot } from './snapshot';

// Repeatedly forwards `planetId`'s clock through whatever action is ready
// next, until `predicate(state)` is false. Used for every "block until some
// condition clears" gate: own build slot busy, global research busy, some
// planet mid-researchLab-upgrade, own ship queue busy.
//
// `captureSnapshots` preserves a legacy inconsistency: some call sites
// attach stateBefore/stateAfter to these wait entries and some don't. This
// isn't a game-logic difference (it's UI-only metadata), so rather than
// silently "fixing" it here, each handler passes the flag matching its own
// original behavior — see plan.md §4 for the full call-site catalog.
export const waitWhile = (state, timeline, recorder, planetId, predicate, { captureSnapshots = false } = {}) => {
  while (predicate(state)) {
    const nextAction = NextActionReady(state, planetId);
    const before = captureSnapshots ? captureStateSnapshot(state, nextAction.payload.planetId) : undefined;
    timeline.push(makeTimelineEntry('wait', state, nextAction, before));
    state = StateFastForward(state, planetId, nextAction.duration, recorder);
    state = CompleteAction(state, nextAction, recorder);
    if (captureSnapshots) {
      timeline[timeline.length - 1].stateAfter = captureStateSnapshot(state, nextAction.payload.planetId);
    }
  }
  return state;
};

// While gathering resources (or waiting out a fixed extra_wait) on
// `planetId`, let any in-flight action that will finish sooner complete
// first, recomputing the remaining threshold after each one (a completed
// action can change production or remaining cost). Stops as soon as the
// next ready action would take *at least* as long as the remaining
// threshold. Returns the final { state, threshold } so the caller can act
// on whatever time is still left to wait out itself.
export const drainShorterThan = (
  state,
  timeline,
  recorder,
  planetId,
  initialThreshold,
  recomputeThreshold,
  { captureSnapshots = false } = {}
) => {
  let threshold = initialThreshold;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const nextAction = NextActionReady(state, planetId);
    if (nextAction === undefined || !(nextAction.duration < threshold)) break;
    const before = captureSnapshots ? captureStateSnapshot(state, nextAction.payload.planetId) : undefined;
    timeline.push(makeTimelineEntry('wait', state, nextAction, before));
    state = StateFastForward(state, planetId, nextAction.duration, recorder);
    state = CompleteAction(state, nextAction, recorder);
    if (captureSnapshots) {
      timeline[timeline.length - 1].stateAfter = captureStateSnapshot(state, nextAction.payload.planetId);
    }
    threshold = recomputeThreshold(state, threshold, nextAction);
  }
  return { state, threshold };
};
